import React, { useState } from 'react';
import { Helmet } from 'react-helmet';

import { Phone, Clock, MapPin, Heart, Users, Calendar, ClipboardCheck, UserCheck, ChevronDown, ChevronUp, Target, Shield, Briefcase, FileText, TrendingUp } from 'lucide-react';
import ReviewsSection from './components/ReviewsSection';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';



const CSAPPage = () => {
  // Constants
  const EMERGENCY_PHONE = '(916) 930-6312';
  const AGENT_NAME = 'Damian Sailors, CA LIC# 1N13041';
  const OFFICE_ADDRESS = '901 H Street, Suite 103, Sacramento, CA 95814';

  // State for expandable sections
  const [expandedSection, setExpandedSection] = useState('overview');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  // CSAP Program Sections with comprehensive content
  const csapSections = [
    {
      id: 'overview',
      icon: Target,
      title: 'Program Overview',
      color: 'from-[#007BFF] to-blue-600',
      borderColor: 'border-blue-300',
      shortDescription: 'A comprehensive support system addressing the complex challenges individuals face after release from custody.',
      paragraphs: [
        {
          title: 'Addressing Post-Release Challenges',
          content: 'The Client Success & Accountability Program (C.S.A.P.) was developed to address the complex challenges individuals face immediately after release from custody. During this transitional period, people often return to unstable environments, unhealthy patterns, or high-risk influences that can undermine their progress and lead to non-compliance with court-ordered conditions. Our program bridges the gap between release and stability by offering structured guidance, consistent oversight, and positive behavioral reinforcement. C.S.A.P. ensures that clients do not face their obligations alone; instead, they receive professional support designed to strengthen accountability, reduce relapse triggers, and help them navigate life after release with clarity and purpose.'
        },
        {
          title: 'Structure, Communication, and Repetition',
          content: 'C.S.A.P. operates on the principle that meaningful change requires structure, communication, and repetition. The program aligns clients with routine behavioral expectations while also giving them tools to develop healthier habits. Through regular check-ins, resource referrals, lifestyle coaching, and strict adherence to court requirements, we help create a stable foundation that individuals can build upon long after their legal matters conclude. Our approach is not punitive; it is supportive, emphasizing progress, stability, and real-life improvement rather than fear or punishment.'
        },
        {
          title: 'Collaborative Process',
          content: 'At its core, C.S.A.P. is a collaborative process. Clients, families, bail agents, and our accountability specialist work together to build a realistic and sustainable plan for success. Each plan is tailored to the individual\'s unique strengths, weaknesses, challenges, and goals. Whether the client is navigating substance-use recovery, emotional instability, transportation barriers, inconsistent employment, or family stress, the program provides clear steps, monitored expectations, and ongoing encouragement to keep them progressing forward.'
        },
        {
          title: 'Supporting the Legal Process',
          content: 'The program is also designed to support the legal process. By documenting compliance, verifying attendance, and generating professional progress summaries, C.S.A.P. provides defense attorneys and judges with credible evidence of responsible behavior. This allows courts to make more informed decisions during hearings, sentencing, or modification requests. The program not only protects the client\'s standing with the court but also reduces risk for the bail agency through increased court appearance rates and decreased violation incidents.'
        },
        {
          title: 'Improving Outcomes for Everyone',
          content: 'Ultimately, C.S.A.P. aims to improve outcomes—for clients, families, the community, the courts, and our agency. It provides structure where there is typically chaos, clarity where there is confusion, and accountability where there is often a lack of direction. By combining behavioral planning with compassionate oversight, the program gives individuals the support they need to make better decisions, avoid destructive habits, and develop the self-discipline necessary for long-term success.'
        }
      ]
    },
    {
      id: 'expectations',
      icon: UserCheck,
      title: 'What You Can Expect',
      color: 'from-[#28A745] to-green-600',
      borderColor: 'border-green-300',
      shortDescription: 'A highly structured, supportive environment tailored to your specific situation with ongoing communication and professional guidance.',
      paragraphs: [
        {
          title: 'Structured and Supportive Environment',
          content: 'Clients entering the Client Success & Accountability Program can expect a highly structured, supportive environment tailored to their specific situation. Upon enrollment, every participant receives a clear introduction to the program, an explanation of expectations, and a breakdown of the tools available to help them remain compliant. This begins with an initial intake conversation in which our accountability specialist gathers information about the client\'s life circumstances, court obligations, behavior patterns, and goals. This intake establishes the foundation of the program and plays a crucial role in determining what level of support and monitoring will be most helpful.'
        },
        {
          title: 'Ongoing Communication',
          content: 'Clients can expect ongoing communication throughout their participation. Weekly check-ins serve as the backbone of the program, offering clients a consistent opportunity to reflect on their past week, identify obstacles, and receive guidance on how to improve. These conversations are structured, professional, and focused on measurable progress. Our specialist ensures that every check-in is documented and that the client fully understands the expectations for the week ahead. Clients can expect the program to be honest, straightforward, and direct in its communication—all with the purpose of helping them meet their legal responsibilities.'
        },
        {
          title: 'Continuous Monitoring of Special Conditions',
          content: 'Participants should also expect continuous monitoring of the special conditions imposed by the court. These conditions may include no-contact orders, curfews, alcohol restrictions, GPS requirements, counseling, or mandated support group attendance. C.S.A.P. makes maintaining these obligations easier by offering assistance with scheduling, meeting reminders, transportation planning, and documentation management. Clients quickly learn that the program is not simply about "checking boxes," but rather about aligning their daily habits with court mandates to prevent violations or misunderstandings.'
        },
        {
          title: 'Active Accountability',
          content: 'Another key expectation is accountability. This program does not operate passively—it actively engages clients to ensure they remain on track. When a client begins to slip, miss meetings, or show signs of instability, our specialist intervenes early to correct the behavior before it escalates into a violation or new arrest. This intervention may involve revising the success plan, adding additional check-ins, involving family (if consent is granted), or providing stronger behavioral recommendations. Clients learn that the program is designed to keep them focused and compliant even when life circumstances become difficult.'
        },
        {
          title: 'Dignity and Respect',
          content: 'Finally, clients can expect to be treated with dignity and respect. The program is not punitive, judgmental, or confrontational. Instead, it is grounded in empathy, structure, and professional guidance. Whether the client is addressing trauma, addiction, emotional distress, or general life instability, the program is built to support—not punish—them. Clients quickly realize that this program is one of the few systems in their legal journey specifically designed to help them succeed rather than penalize them for shortcomings.'
        }
      ]
    },
    {
      id: 'checkins',
      icon: Calendar,
      title: 'Weekly Check-Ins',
      color: 'from-purple-500 to-purple-600',
      borderColor: 'border-purple-300',
      shortDescription: 'Structured weekly conversations that provide accountability, guidance, and measurable progress tracking.',
      paragraphs: [
        {
          title: 'The Cornerstone of the Program',
          content: 'Weekly check-ins are the cornerstone of the Client Success & Accountability Program. These conversations provide a predictable and reliable system for evaluating the client\'s behavior, stability, and compliance. Each check-in is scheduled in advance, creating a rhythm and structure that clients can depend on. During the call, our specialist reviews all major areas of the client\'s obligations and well-being, ensuring that no aspect of the client\'s progress goes overlooked. This consistency gives the client confidence, direction, and a sense of accountability that many have never experienced before.'
        },
        {
          title: 'Court-Related Responsibilities',
          content: 'The check-in process begins with verification of all court-related responsibilities. This includes reviewing special conditions such as no-contact orders, mandated programs, curfews, or substance restrictions. Our specialist confirms whether the client attended all required meetings, identifies any missed obligations, and determines what additional steps need to be taken to remain compliant. This early review prevents miscommunication or accidental violations and helps maintain clarity regarding the client\'s duties.'
        },
        {
          title: 'Lifestyle and Behavioral Progress',
          content: 'The next phase of the check-in focuses on the client\'s lifestyle and behavioral progress. Our specialist examines areas such as sleep schedules, stress triggers, exercise routines, employment status, peer interactions, and sobriety management. The goal is to identify patterns that may lead to setbacks or increased risk. Our specialist helps the client develop strategies to overcome obstacles, avoid high-risk situations, and replace negative behaviors with healthier alternatives. Each discussion results in a set of practical action items that the client can apply over the coming week.'
        },
        {
          title: 'Encouragement and Positive Reinforcement',
          content: 'Clients also receive encouragement and positive reinforcement during these sessions. Many individuals in the criminal justice system struggle with feelings of discouragement, guilt, or hopelessness. Weekly check-ins help counteract these feelings by recognizing progress, reinforcing strengths, and celebrating even small wins. This approach builds motivation and enhances the client\'s sense of self-efficacy. Over time, the client begins to internalize the idea that they are capable of controlling their actions and shaping their future.'
        },
        {
          title: 'Meticulous Documentation',
          content: 'All check-ins are meticulously documented and stored in the client\'s file. This documentation becomes invaluable for courts and attorneys who need evidence of the client\'s progress. It also provides a timeline of behavioral changes and compliance patterns, helping the program adjust support levels as needed. Through this system, check-ins become much more than a simple conversation—they become a structured, measurable part of the client\'s behavioral transformation and legal success.'
        }
      ]
    },
    {
      id: 'compliance',
      icon: ClipboardCheck,
      title: 'Compliance Tracking',
      color: 'from-amber-500 to-orange-600',
      borderColor: 'border-amber-300',
      shortDescription: 'Comprehensive monitoring system that ensures clients understand and meet all court-ordered conditions.',
      paragraphs: [
        {
          title: 'Most Important Function',
          content: 'Compliance tracking is one of the most important functions of the Client Success & Accountability Program. Courts impose conditions for a reason—to protect the community, ensure accountability, and reduce the risk of future offenses. C.S.A.P. takes these conditions seriously and develops a personalized monitoring system for each client. This ensures that the client clearly understands what is required and that the program can actively document adherence. The goal is to prevent violations before they occur by providing structure, reminders, and consistent evaluation.'
        },
        {
          title: 'Thorough Review of Special Conditions',
          content: 'The compliance process begins with a thorough review of all special conditions issued by the court. This includes restrictions such as GPS monitoring, travel limitations, curfews, therapy mandates, and support group attendance. Our specialist creates a detailed compliance profile for each client and integrates these requirements into their weekly success plan. By consolidating all obligations in a clear format, clients are less likely to overlook or misunderstand their responsibilities.'
        },
        {
          title: 'Multi-Channel Verification',
          content: 'Throughout the week, our specialist verifies compliance through multiple channels. This may include confirming attendance with treatment centers, reviewing time-stamped meeting slips, gathering proof of sobriety support participation, and ensuring adherence to no-contact orders. When required, our specialist communicates directly with approved third-party service providers to confirm participation. This meticulous approach ensures that all monitored activities are accurately documented and can withstand judicial scrutiny.'
        },
        {
          title: 'Quick Intervention',
          content: 'When a client begins to show signs of non-compliance—missed meetings, late notifications, or gaps in documentation—the program acts quickly. Our specialist conducts an immediate intervention, contacting the client to determine the cause, addressing obstacles, and reinforcing the importance of compliance. In cases where non-compliance may lead to legal consequences, our specialist communicates with the supervising bail agent to establish next steps. This proactive intervention prevents minor missteps from escalating into major violations.'
        },
        {
          title: 'Structured Reporting System',
          content: 'All compliance information is organized into a structured reporting system that allows attorneys and judges to easily interpret the client\'s progress. These records can be used during hearings, modification requests, sentencing, or treatment evaluations. Judges often recognize structured compliance tracking as a strong indicator of personal responsibility and rehabilitation. For the client, this documentation not only protects them from misunderstandings but also demonstrates a commitment to stability and growth.'
        }
      ]
    },
    {
      id: 'family',
      icon: Users,
      title: 'Family Support',
      color: 'from-pink-500 to-rose-600',
      borderColor: 'border-pink-300',
      shortDescription: 'Structured communication and guidance for families to reduce household stress and improve client success.',
      paragraphs: [
        {
          title: 'Critical Component',
          content: 'Family support is a critical component of the Client Success & Accountability Program. Many clients return home to families who are overwhelmed, confused about the legal process, or unsure how to help. When a client consents to family involvement, C.S.A.P. provides structured communication and guidance designed to reduce stress on the household and improve the client\'s chance of success. Families often want to help but lack the tools or knowledge to support a loved one effectively after release. The program fills this gap by offering clarity, consistency, and direction.'
        },
        {
          title: 'Assessing Family Role',
          content: 'During the initial intake, our specialist assesses the family\'s role and the level of support available in the home environment. Some clients have strong family support systems, while others experience conflict or instability at home. Understanding this dynamic allows the program to offer recommendations that strengthen communication and reduce tension. When family involvement is beneficial, C.S.A.P. ensures everyone understands the client\'s conditions, weekly expectations, and behavioral goals.'
        },
        {
          title: 'Periodic Updates',
          content: 'Families receive periodic updates about the client\'s progress when authorized by the client. These updates may include attendance confirmations, behavioral milestones, concerns, or areas where the client may need additional structure at home. This transparency helps families stay aligned with the program and creates a unified approach to accountability. Many families report feeling relieved once they understand how to support the client\'s obligations effectively.'
        },
        {
          title: 'Education on Warning Signs',
          content: 'The program also educates families on how to recognize early warning signs of relapse, emotional instability, or crisis behavior. This empowers them to intervene early or notify the program when the client may need additional support. Families learn strategies for establishing boundaries, reducing conflict, and reinforcing positive routines. This level of involvement often leads to healthier communication and improved family relationships.'
        },
        {
          title: 'Enhancing Long-Term Success',
          content: 'Ultimately, family support enhances the client\'s likelihood of long-term success. When clients know their families are engaged, informed, and aligned with the program, they feel more responsible and motivated to stay on track. A supportive home environment helps reduce relapse, reinforces accountability, and increases overall stability. C.S.A.P. views family involvement not just as optional—but as a powerful tool that strengthens the program\'s effectiveness and provides clients with the emotional foundation they need to move forward.'
        }
      ]
    }
  ];

  // Program benefits for quick reference
  const programBenefits = [
    {
      icon: Shield,
      title: 'Higher Court Appearance Rates',
      description: 'Structured accountability leads to better compliance'
    },
    {
      icon: TrendingUp,
      title: 'Reduced Re-arrest Risk',
      description: 'Early intervention prevents escalation'
    },
    {
      icon: FileText,
      title: 'Professional Documentation',
      description: 'Court-ready progress reports'
    },
    {
      icon: Heart,
      title: 'Family Support',
      description: 'Aligned approach to accountability'
    },
    {
      icon: Briefcase,
      title: 'Life Stabilization',
      description: 'Employment, housing, and routine building'
    },
    {
      icon: Users,
      title: 'Community Safety',
      description: 'Responsible reintegration support'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Client Success & Accountability Program (CSAP) | Sailors Bail Bonds</title>
        <meta 
          name="description" 
          content="Comprehensive post-release support program including weekly check-ins, compliance tracking, family support, and life stabilization. Reduce recidivism and ensure court compliance." 
        />
        <meta 
          name="keywords" 
          content="bail accountability program, post-release support, court compliance, family support, Sacramento bail bonds, CSAP" 
        />
      </Helmet>

      <div className="flex w-full flex-col items-center bg-gray-50">
        <Header />
        
        {/* Hero Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#007BFF] via-blue-600 to-blue-700">
          <div className="max-w-7xl mx-auto text-center">
            <div className="inline-block bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-2xl px-6 py-3 mb-6">
              <p className="text-white font-bold text-lg" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                A Sailors Bail Bonds Exclusive
              </p>
            </div>
            <h2 
              className="text-5xl lg:text-6xl font-black text-white mb-6 leading-tight"
              style={{ fontFamily: 'Roboto Slab, serif' }}
            >
              Client Success & Accountability Program
            </h2>
            <p 
              className="text-2xl text-blue-100 leading-relaxed max-w-4xl mx-auto mb-8"
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              Supporting clients. Reducing risk. Improving lives through structured post-release guidance and accountability.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href={`tel:${EMERGENCY_PHONE?.replace(/\D/g, '')}`}
                className="bg-white text-[#007BFF] px-8 py-4 rounded-xl font-black text-lg hover:bg-blue-50 transition-all transform hover:scale-105 shadow-xl flex items-center space-x-2"
              >
                <Phone className="w-6 h-6" />
                <span>Enroll Now: {EMERGENCY_PHONE}</span>
              </a>
              <a 
                href="#program-details"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-black text-lg hover:bg-white/10 transition-all flex items-center space-x-2"
              >
                <span>Learn More</span>
                <ChevronDown className="w-6 h-6" />
              </a>
            </div>
          </div>
        </section>

        {/* Program Benefits Grid */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h3 
                className="text-4xl font-black text-gray-900 mb-4"
                style={{ fontFamily: 'Roboto Slab, serif' }}
              >
                Why Courts & Families Prefer C.S.A.P.
              </h3>
              <p 
                className="text-xl text-gray-600"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                A real opportunity for positive change through structured accountability
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {programBenefits?.map((benefit, idx) => {
                const BenefitIcon = benefit?.icon;
                return (
                  <div 
                    key={idx}
                    className="bg-gradient-to-br from-blue-50 to-white border-2 border-[#007BFF]/20 rounded-2xl p-8 hover:shadow-xl transition-all"
                  >
                    <div className="bg-gradient-to-br from-[#007BFF] to-blue-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                      <BenefitIcon className="w-8 h-8 text-white" />
                    </div>
                    <h4 
                      className="text-xl font-black text-gray-900 mb-3"
                      style={{ fontFamily: 'Roboto Slab, serif' }}
                    >
                      {benefit?.title}
                    </h4>
                    <p 
                      className="text-base text-gray-700"
                      style={{ fontFamily: 'Open Sans, sans-serif' }}
                    >
                      {benefit?.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Detailed Program Sections */}
        <section id="program-details" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#F8F9FA]">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h3 
                className="text-4xl font-black text-gray-900 mb-4"
                style={{ fontFamily: 'Roboto Slab, serif' }}
              >
                Comprehensive Program Details
              </h3>
              <p 
                className="text-xl text-gray-600"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                Click each section to learn more about our structured approach
              </p>
            </div>

            <div className="space-y-6">
              {csapSections?.map((section) => {
                const SectionIcon = section?.icon;
                const isExpanded = expandedSection === section?.id;
                
                return (
                  <div 
                    key={section?.id}
                    className={`bg-white border-2 ${section?.borderColor} rounded-2xl overflow-hidden shadow-lg transition-all`}
                  >
                    {/* Section Header - Clickable */}
                    <button
                      onClick={() => toggleSection(section?.id)}
                      className="w-full text-left p-8 hover:bg-gray-50 transition-colors"
                      aria-expanded={isExpanded}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 flex-1">
                          <div className={`bg-gradient-to-br ${section?.color} w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0`}>
                            <SectionIcon className="w-8 h-8 text-white" />
                          </div>
                          <div className="flex-1">
                            <h4 
                              className="text-2xl font-black text-gray-900 mb-2"
                              style={{ fontFamily: 'Roboto Slab, serif' }}
                            >
                              {section?.title}
                            </h4>
                            <p 
                              className="text-base text-gray-600"
                              style={{ fontFamily: 'Open Sans, sans-serif' }}
                            >
                              {section?.shortDescription}
                            </p>
                          </div>
                        </div>
                        <div className="ml-4 flex-shrink-0">
                          {isExpanded ? (
                            <ChevronUp className="w-8 h-8 text-[#007BFF]" />
                          ) : (
                            <ChevronDown className="w-8 h-8 text-[#007BFF]" />
                          )}
                        </div>
                      </div>
                    </button>

                    {/* Expandable Content */}
                    {isExpanded && (
                      <div className="px-8 pb-8 space-y-6 border-t-2 border-gray-100 pt-8">
                        {section?.paragraphs?.map((para, idx) => (
                          <div key={idx} className="space-y-3">
                            {para?.title && (
                              <h5 
                                className="text-xl font-bold text-gray-900"
                                style={{ fontFamily: 'Roboto Slab, serif' }}
                              >
                                {para?.title}
                              </h5>
                            )}
                            <p 
                              className="text-base text-gray-700 leading-relaxed"
                              style={{ fontFamily: 'Open Sans, sans-serif' }}
                            >
                              {para?.content}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#007BFF] via-blue-600 to-blue-700">
          <div className="max-w-4xl mx-auto text-center">
            <h3 
              className="text-4xl font-black text-white mb-6"
              style={{ fontFamily: 'Roboto Slab, serif' }}
            >
              Ready to Get Started?
            </h3>
            <p 
              className="text-xl text-blue-100 mb-8 leading-relaxed"
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              Contact us today to enroll in the Client Success & Accountability Program and give your loved one the support they need for a successful recovery.
            </p>
            
            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-2xl p-8 mb-8">
              <div className="space-y-4">
                <div className="flex items-center justify-center space-x-3">
                  <UserCheck className="w-6 h-6 text-white" />
                  <p className="text-white font-bold text-lg" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    Program Coordinator: {AGENT_NAME}
                  </p>
                </div>
                <div className="flex items-center justify-center space-x-3">
                  <Phone className="w-6 h-6 text-white" />
                  <a 
                    href={`tel:${EMERGENCY_PHONE?.replace(/\D/g, '')}`}
                    className="text-white font-black text-2xl hover:text-blue-200 transition-colors"
                    style={{ fontFamily: 'Roboto Slab, serif' }}
                  >
                    {EMERGENCY_PHONE}
                  </a>
                </div>
                <div className="flex items-center justify-center space-x-3">
                  <MapPin className="w-6 h-6 text-white" />
                  <p className="text-white font-semibold text-base" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    {OFFICE_ADDRESS}
                  </p>
                </div>
                <div className="flex items-center justify-center space-x-3">
                  <Clock className="w-6 h-6 text-white" />
                  <p className="text-white font-semibold text-base" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    Available 24/7 - Always Here to Help
                  </p>
                </div>
              </div>
            </div>

            <a 
              href={`tel:${EMERGENCY_PHONE?.replace(/\D/g, '')}`}
              className="bg-white text-[#007BFF] px-12 py-5 rounded-2xl font-black text-2xl hover:bg-blue-50 transition-all transform hover:scale-105 shadow-2xl inline-flex items-center space-x-3"
            >
              <Phone className="w-8 h-8" />
              <span>Call Now to Enroll</span>
            </a>
          </div>
        </section>

        {/* Reviews Section */}
        <ReviewsSection />

        <Footer />
      </div>
    </>
  );
};

export default CSAPPage;